import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, Bot, Check, CheckCircle2, Clock, ExternalLink, Maximize2, MessageSquarePlus, Send, Sparkles, X, XCircle } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { formatEuro } from '@/lib/format';
import { Badge, Button, Spinner } from '@/components/ui';
import { Markdown } from './Markdown';
import { useAssistant, type AssistantAction, type AssistantMessage, type AssistantTable } from './useAssistant';

/**
 * Chat mit dem KI-Assistenten – gemeinsam genutzt vom schwebenden Panel und der Seite /assistent.
 */

const TABLE_PREVIEW_ROWS = 8;

function Cell({ value }: { value: string | number | null }) {
  if (value === null || value === undefined || value === '') return <span className="text-slate-400">–</span>;
  if (typeof value === 'string' && /^-?\d+\.\d{2}$/.test(value)) return <span className="tabular-nums whitespace-nowrap">{formatEuro(value)}</span>;
  return <>{String(value)}</>;
}

function ResultTable({ table, onNavigate }: { table: AssistantTable; onNavigate?: () => void }) {
  const [all, setAll] = useState(false);
  if (!table.rows.length) return null;
  const rows = all ? table.rows : table.rows.slice(0, TABLE_PREVIEW_ROWS);
  const numeric = table.columns.map((_, i) => table.rows.every((r) => r[i] === null || typeof r[i] === 'number' || /^-?\d+(\.\d{2})?$/.test(String(r[i]))));
  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-1.5">
        <span className="text-xs font-semibold text-slate-600">{table.title}</span>
        <span className="text-[11px] text-slate-400">{table.total} Treffer</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100 text-xs">
          <thead>
            <tr>
              {table.columns.map((c, i) => (
                <th key={c} className={clsx('whitespace-nowrap px-2.5 py-1.5 font-medium text-slate-500', numeric[i] ? 'text-right' : 'text-left')}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, ri) => (
              <tr key={ri} className="hover:bg-slate-50">
                {r.map((v, ci) => (
                  <td key={ci} className={clsx('px-2.5 py-1.5 text-slate-700', numeric[ci] && 'text-right', String(v ?? '').length <= 24 && 'whitespace-nowrap')}>
                    {ci === 0 && table.rowLinks?.[ri] ? (
                      <Link to={table.rowLinks[ri]!} onClick={onNavigate} className="font-medium text-brand-700 hover:underline">
                        <Cell value={v} />
                      </Link>
                    ) : (
                      <Cell value={v} />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.rows.length > TABLE_PREVIEW_ROWS && (
        <button type="button" onClick={() => setAll((a) => !a)} className="w-full border-t border-slate-100 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50">
          {all ? 'Weniger anzeigen' : `Alle ${table.rows.length} Zeilen anzeigen`}
        </button>
      )}
    </div>
  );
}

const ACTION_STATE: Record<string, { label: string; tone: 'blue' | 'green' | 'gray' | 'red' | 'yellow' }> = {
  VORGESCHLAGEN: { label: 'Wartet auf Bestätigung', tone: 'blue' },
  BESTAETIGT: { label: 'Ausgeführt', tone: 'green' },
  ABGELEHNT: { label: 'Abgelehnt', tone: 'gray' },
  ABGELAUFEN: { label: 'Abgelaufen', tone: 'gray' },
  FEHLGESCHLAGEN: { label: 'Fehlgeschlagen', tone: 'red' },
};

function ActionCard({
  action,
  onConfirm,
  onReject,
  busy,
  error,
  onNavigate,
}: {
  action: AssistantAction;
  onConfirm: () => void;
  onReject: () => void;
  busy: boolean;
  error: string | null;
  onNavigate?: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (action.status !== 'VORGESCHLAGEN') return;
    const t = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(t);
  }, [action.status]);
  const expired = action.status === 'VORGESCHLAGEN' && new Date(action.expiresAt).getTime() < now;
  const status = expired ? 'ABGELAUFEN' : action.status;
  const state = ACTION_STATE[status] ?? ACTION_STATE.VORGESCHLAGEN!;
  const open = status === 'VORGESCHLAGEN';

  return (
    <div className={clsx('mt-2 rounded-lg border bg-white shadow-sm', open ? 'border-brand-300 ring-1 ring-brand-100' : 'border-slate-200')}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
        <span className="text-sm font-semibold text-slate-800">{action.preview.title}</span>
        <Badge tone={state.tone} dot>
          {state.label}
        </Badge>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 px-3 py-2 text-xs">
        {action.preview.lines.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-slate-500">{k}</dt>
            <dd className="font-medium text-slate-800">{v}</dd>
          </div>
        ))}
      </dl>
      {action.preview.warning && open && (
        <p className="mx-3 mb-2 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {action.preview.warning}
        </p>
      )}
      {status === 'BESTAETIGT' && action.result && (
        <p className="mx-3 mb-2 flex items-start gap-1.5 rounded-md bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {action.result.message}
            {action.result.link && (
              <>
                {' '}
                <Link to={action.result.link} onClick={onNavigate} className="font-medium underline">
                  Öffnen
                </Link>
              </>
            )}
          </span>
        </p>
      )}
      {status === 'FEHLGESCHLAGEN' && action.error && (
        <p className="mx-3 mb-2 flex items-start gap-1.5 rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">
          <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {action.error}
        </p>
      )}
      {error && open && <p className="mx-3 mb-2 rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</p>}
      {open && (
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-2">
          <span className="flex items-center gap-1 text-[11px] text-slate-400">
            <Clock className="h-3 w-3" /> gültig bis {new Date(action.expiresAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
          </span>
          <div className="flex gap-2">
            <Button size="xs" variant="ghost" onClick={onReject} disabled={busy}>
              Ablehnen
            </Button>
            <Button size="xs" variant="success" icon={<Check className="h-3.5 w-3.5" />} onClick={onConfirm} loading={busy}>
              Bestätigen
            </Button>
          </div>
        </div>
      )}
      {!open && action.preview.link && status !== 'BESTAETIGT' && (
        <div className="border-t border-slate-100 px-3 py-1.5 text-right">
          <Link to={action.preview.link} onClick={onNavigate} className="text-xs font-medium text-brand-700 hover:underline">
            Zur Seite
          </Link>
        </div>
      )}
    </div>
  );
}

function errorText(e: unknown) {
  return e instanceof ApiError ? e.message : 'Die Anfrage ist fehlgeschlagen. Bitte erneut versuchen.';
}

export function AssistantChat({ variant, onClose, onNavigate }: { variant: 'panel' | 'page'; onClose?: () => void; onNavigate?: () => void }) {
  const { status, conversation, send, confirm, reject, reset } = useAssistant();
  const [text, setText] = useState('');
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messages: AssistantMessage[] = conversation.data?.messages ?? [];

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, send.isPending]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = (value?: string) => {
    const t = (value ?? text).trim();
    if (!t || send.isPending) return;
    setText('');
    // Bei Fehler den Text wiederherstellen, damit nichts verloren geht
    send.mutate(t, { onError: () => setText((cur) => cur || t) });
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  const doConfirm = (id: string) =>
    confirm.mutate(id, {
      onSuccess: () => setActionErrors((m) => ({ ...m, [id]: '' })),
      onError: (e) => setActionErrors((m) => ({ ...m, [id]: errorText(e) })),
    });
  const doReject = (id: string) => reject.mutate(id, { onError: (e) => setActionErrors((m) => ({ ...m, [id]: errorText(e) })) });

  const suggestions = status.data?.suggestions ?? [];
  const mode = status.data?.mode;

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-brand-700">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">KI-Assistent</p>
          <p className="truncate text-[11px] text-slate-500">{mode === 'openai' ? `OpenAI · ${status.data?.model ?? ''}` : mode ? 'Regelbasierter Modus' : '…'}</p>
        </div>
        <Button size="xs" variant="ghost" icon={<MessageSquarePlus className="h-4 w-4" />} onClick={() => reset.mutate()} loading={reset.isPending} title="Neues Gespräch beginnen">
          <span className="hidden sm:inline">Neues Gespräch</span>
        </Button>
        {variant === 'panel' && (
          <>
            <Link to="/assistent" onClick={onNavigate} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" title="Als Seite öffnen" aria-label="Als Seite öffnen">
              <Maximize2 className="h-4 w-4" />
            </Link>
            <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Schließen" title="Schließen">
              <X className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4" aria-live="polite">
        {conversation.isLoading && (
          <div className="flex justify-center py-10">
            <Spinner className="h-5 w-5" />
          </div>
        )}
        {!conversation.isLoading && messages.length === 0 && (
          <div className="flex flex-col items-center py-6 text-center">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-700">
              <Bot className="h-6 w-6" />
            </span>
            <p className="font-medium text-slate-800">Wie kann ich helfen?</p>
            <p className="mt-1 max-w-sm text-sm text-slate-500">
              Fragen Sie nach Kennzahlen, Rückständen oder Mahnungen. Buchungen und Freigaben schlage ich nur vor – ausgeführt wird erst nach Ihrer Bestätigung.
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => submit(s)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 shadow-sm hover:border-brand-300 hover:bg-brand-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) =>
          m.role === 'user' ? (
            <div key={m.id} className="flex flex-col items-end">
              <div className={clsx('max-w-[85%] whitespace-pre-line rounded-2xl rounded-tr-sm px-3.5 py-2 text-sm', m.error ? 'bg-red-50 text-red-800 ring-1 ring-red-200' : 'bg-brand-700 text-white', m.pending && 'opacity-80')}>
                {m.text}
              </div>
              {m.error && <p className="mt-1 text-[11px] text-red-600">{errorText(send.error)}</p>}
            </div>
          ) : (
            <div key={m.id} className="flex gap-2">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
                <Bot className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="rounded-2xl rounded-tl-sm bg-slate-100 px-3.5 py-2 text-sm text-slate-800">
                  <Markdown text={m.text} onNavigate={onNavigate} />
                </div>
                {m.tables.map((t, i) => (
                  <ResultTable key={i} table={t} onNavigate={onNavigate} />
                ))}
                {m.actions.map((a) => (
                  <ActionCard
                    key={a.id}
                    action={a}
                    busy={(confirm.isPending && confirm.variables === a.id) || (reject.isPending && reject.variables === a.id)}
                    error={actionErrors[a.id] || null}
                    onConfirm={() => doConfirm(a.id)}
                    onReject={() => doReject(a.id)}
                    onNavigate={onNavigate}
                  />
                ))}
                {m.notice && <p className="mt-1 text-[11px] text-slate-400">{m.notice}</p>}
              </div>
            </div>
          ),
        )}
        {send.isPending && (
          <div className="flex items-center gap-2 pl-9 text-sm text-slate-500">
            <Spinner className="h-4 w-4" /> Einen Moment …
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form
        className="border-t border-slate-200 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            rows={1}
            maxLength={2000}
            className="input-base max-h-32 min-h-[40px] flex-1 resize-none"
            placeholder="Nachricht an den Assistenten …"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            aria-label="Nachricht"
          />
          <Button type="submit" variant="primary" icon={<Send className="h-4 w-4" />} disabled={!text.trim()} loading={send.isPending} aria-label="Senden" />
        </div>
        {status.data && (
          <p className="mt-1.5 flex items-start gap-1 text-[10.5px] leading-snug text-slate-400">
            <ExternalLink className="mt-px h-3 w-3 shrink-0" />
            {status.data.notice}
          </p>
        )}
      </form>
    </div>
  );
}
